import React from 'react'
import ReactDOM from 'react-dom/client'
// Variable Source fonts — the theme's font stack references "Source Serif 4 Variable" / "Source Sans 3
// Variable" first (crisp, full weight range). Without these imports the app fell back to the heavier
// non-variable Google fonts (serif only at 600/700), which looked off vs. the design. web/desktop do
// the same import.
import '@fontsource-variable/source-sans-3'
import '@fontsource-variable/source-serif-4'
import '@fontsource-variable/source-code-pro'
// Lora — the "RivalOS" wordmark in the nav rail uses this (self-hosted, not the Google CDN, to match
// the repo's Fontsource convention + offline support).
import '@fontsource/lora/500.css'
import '@fontsource/lora/600.css'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AppThemeProvider } from '@reachinsight-app/shared/src/theme/ThemeProvider'
import { PostHogProvider } from '@reachinsight-app/shared/src'
import { AppShell } from './AppShell'
import { HomePage } from './pages/HomePage'
import { ChatsPage } from './pages/ChatsPage'
import { ArtifactsPage } from './pages/ArtifactsPage'
import { ArtifactViewPage } from './pages/ArtifactViewPage'
import { ProjectsPage } from './pages/ProjectsPage'
import { ProjectDetailPage } from './pages/ProjectDetailPage'
import { PlaceholderPage } from './pages/PlaceholderPage'
import { RegistryPage } from './pages/RegistryPage'
import { SkillsPage } from './pages/SkillsPage'
import { ExtensionsPage } from './pages/ExtensionsPage'
import { AppViewerPage } from './pages/AppViewerPage'
import { GadgetViewerPage } from './pages/GadgetViewerPage'
import {RosFeatureProvider} from './access/RosFeatureProvider'
import {AccessAwareRoute} from './access/AccessAwareRoute'
import {useFeatureFlag, usePostHog} from '@reachinsight-app/shared/src'
import {SlackConnectPage} from './pages/SlackConnectPage'
import {captureSlackLinkToken} from './slackLinkToken'

const ROS_BROWSER_FEATURE_FLAG_KEYS = ['ros-oauth-enabled', 'ros-extensions'] as const

// Strip the one-time Slack credential before PostHog or any other provider can observe the URL.
captureSlackLinkToken()

/** Extensions remain an experimental browser-gated surface, separate from server-authorized capabilities. */
function ExtensionRoute({children}: {children: React.ReactNode}) {
  const { areFlagsLoaded } = usePostHog()
  const extensionsOn = useFeatureFlag('ros-extensions')
  if (!areFlagsLoaded) return null
  if (!extensionsOn) return <Navigate to="/" replace />
  return <>{children}</>
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <PostHogProvider featureFlagKeys={ROS_BROWSER_FEATURE_FLAG_KEYS}>
        <AppThemeProvider>
          <RosFeatureProvider>
          <Routes>
            {/* Direct library opens use a clean full-window viewer, outside the chat shell. */}
            <Route path="/artifact/:artifactId" element={<ArtifactViewPage />} />
            {/* AppShell is the layout route: persistent rail + always-mounted ChatWorkspace + Outlet.
                On /chat* the Outlet renders nothing and the workspace shows; other routes overlay it. */}
            <Route element={<AppShell />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/chat" element={null} />
              <Route path="/chat/:sessionId" element={null} />
              <Route path="/chats" element={<ChatsPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
              <Route path="/artifacts" element={<ArtifactsPage />} />
              <Route path="/skills" element={<AccessAwareRoute feature="ros-skills"><SkillsPage /></AccessAwareRoute>} />
              <Route path="/registry" element={<AccessAwareRoute feature="ros-registry"><RegistryPage /></AccessAwareRoute>} />
              <Route path="/extensions" element={<ExtensionRoute><ExtensionsPage /></ExtensionRoute>} />
              <Route path="/extensions/:definitionId" element={<ExtensionRoute><ExtensionsPage /></ExtensionRoute>} />
              <Route path="/apps/:id" element={<ExtensionRoute><AppViewerPage /></ExtensionRoute>} />
              <Route path="/gadgets/:id" element={<ExtensionRoute><GadgetViewerPage /></ExtensionRoute>} />
              <Route path="/settings" element={<PlaceholderPage title="Settings" />} />
              <Route path="/slack/connect" element={<AccessAwareRoute feature="ros-slack"><SlackConnectPage /></AccessAwareRoute>} />
              <Route path="*" element={<HomePage />} />
            </Route>
          </Routes>
          </RosFeatureProvider>
        </AppThemeProvider>
      </PostHogProvider>
    </BrowserRouter>
  </React.StrictMode>,
)
